import { Controller } from "@hotwired/stimulus";
import { InspectionQrCodesApi } from "../javascripts/api/api";
import { Configuration } from "../javascripts/api/configuration";

export default class extends Controller {
  static targets = [
    "subGroupSelect",
    "siteSelect",
    "resourceSearchField",
    "checkListSearchField",
    "resourceItem",
    "checkListItem",
    "confirmDialog",
    "confirmMessage",
  ];
  static values = { customerId: String, basePath: String, fetchFailedMessage: String };

  declare readonly subGroupSelectTarget: HTMLSelectElement;
  declare readonly siteSelectTarget: HTMLSelectElement;
  declare readonly resourceSearchFieldTarget: HTMLInputElement;
  declare readonly checkListSearchFieldTarget: HTMLInputElement;
  declare readonly resourceItemTargets: HTMLElement[];
  declare readonly checkListItemTargets: HTMLElement[];
  declare readonly confirmDialogTarget: HTMLDialogElement;
  declare readonly confirmMessageTarget: HTMLElement;
  declare readonly customerIdValue: string;
  declare readonly basePathValue: string;
  declare readonly fetchFailedMessageValue: string;

  private bypassConfirm = false;

  async confirmBeforeSubmit(event: SubmitEvent): Promise<void> {
    if (this.bypassConfirm) return;

    const form = this.element as HTMLFormElement;

    // 必須項目が未選択の場合はプレビュータブを開かず、現在のタブへの
    // デフォルト送信に任せてサーバー側のバリデーションエラーを表示する
    if (!this.hasRequiredSelections()) {
      form.target = "_self";
      return;
    }

    event.preventDefault();

    this.setFormDisabled(form, true);
    const result = await this.fetchConfirmMessage();
    this.setFormDisabled(form, false);

    // 取得失敗時は削除される既存QRコードの有無が不明なため、安全側に倒して確認を取る
    const message = result === null ? this.fetchFailedMessageValue : result.message;

    if (message) {
      // 新規タブはまだ開かず、元のタブ上のモーダルで確認を取る
      this.confirmMessageTarget.textContent = message;
      this.confirmDialogTarget.showModal();
      return;
    }

    this.submitToPreviewTab();
  }

  acceptConfirm(): void {
    this.confirmDialogTarget.close();
    this.submitToPreviewTab();
  }

  cancelConfirm(): void {
    this.confirmDialogTarget.close();
  }

  // モーダル内のコンテンツはp-6を持つ内側のdivが覆っているため、
  // クリックがdialog要素自体に届くのは背景(::backdrop)を押したときだけ
  closeOnBackdropClick(event: MouseEvent): void {
    if (event.target === this.confirmDialogTarget) this.cancelConfirm();
  }

  // ユーザーのクリック操作から同期的にtarget="_blank"で送信することで、
  // ポップアップブロッカーにブロックされずに新規タブでプレビューを開く
  private submitToPreviewTab(): void {
    const form = this.element as HTMLFormElement;
    form.target = "_blank";
    this.bypassConfirm = true;
    form.requestSubmit();
    this.bypassConfirm = false;
  }

  private hasRequiredSelections(): boolean {
    const hasSite = this.siteSelectTarget.value !== "";
    const hasResource = this.resourceItemTargets.some((el) => el.querySelector('input[type="checkbox"]:checked'));
    const hasCheckList = this.checkListItemTargets.some((el) => el.querySelector('input[type="checkbox"]:checked'));
    return hasSite && hasResource && hasCheckList;
  }

  private setFormDisabled(form: HTMLFormElement, disabled: boolean): void {
    Array.from(form.elements).forEach((element) => {
      (element as HTMLInputElement | HTMLSelectElement | HTMLButtonElement).disabled = disabled;
    });
  }

  private checkedValues(targets: HTMLElement[]): string[] {
    return targets.flatMap((el) =>
      Array.from(el.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked')).map((checkbox) => checkbox.value),
    );
  }

  // 取得に失敗した場合はnullを返す（成功時のメッセージなしとは区別する）
  private async fetchConfirmMessage(): Promise<{ message: string | null } | null> {
    const api = new InspectionQrCodesApi(new Configuration({ basePath: this.basePathValue }));

    try {
      const response = await api.confirmInspectionQrCodes(this.customerIdValue, {
        siteId: this.siteSelectTarget.value,
        resourceIds: this.checkedValues(this.resourceItemTargets),
        checkListIds: this.checkedValues(this.checkListItemTargets),
      });
      return response.data;
    } catch {
      return null;
    }
  }

  subGrpChanged(): void {
    const selected = this.subGroupSelectTarget.value;
    this.resourceItemTargets.forEach((el) => {
      const subGrp = el.dataset.subGroupId || "";
      el.style.display = selected === "" || subGrp === "" || subGrp === selected ? "" : "none";
    });

    this.checkListItemTargets.forEach((el) => {
      const subGrp = el.dataset.subGroupId || "";
      el.style.display = selected === "" || subGrp === "" || subGrp === selected ? "" : "none";
    });
  }

  filterByResource(): void {
    const text = this.resourceSearchFieldTarget.value.toLowerCase();
    this.resourceItemTargets.forEach((el) => {
      const name = el.dataset.searchText || "";
      el.style.display = name.toLowerCase().includes(text) ? "" : "none";
    });
  }

  filterByCheckList(): void {
    const text = this.checkListSearchFieldTarget.value.toLowerCase();
    this.checkListItemTargets.forEach((el) => {
      const name = el.dataset.searchText || "";
      el.style.display = name.toLowerCase().includes(text) ? "" : "none";
    });
  }
}
